import React, { useEffect } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { GeoJsonFeature } from '../../utils/ogc';
import { WebMercatorViewport } from '@deck.gl/core';





export function BoundsListener({ onChange }: { onChange: (b: { south: number; west: number; north: number; east: number }) => void }) {
  const map = useMap()
  const fn = React.useRef(onChange)
  fn.current = onChange;
  useEffect(() => {
    if (!map) return
    const start = new Date();
    const emit = () => {
      if (new Date().getTime() - start.getTime() < 10000) return;
      const b = map.getBounds?.()
      if (!b) return
      const ne = b.getNorthEast?.()
      const sw = b.getSouthWest?.()
      if (!ne || !sw) return
      fn.current({ south: sw.lat(), west: sw.lng(), north: ne.lat(), east: ne.lng() })
    }
    // emit()
    const idle = map.addListener('idle', emit)
    return () => idle?.remove?.()
  }, [map])
  return null
}


export function CameraListener({ onChange }: { onChange: (v: { latitude: number; longitude: number; zoom: number; bearing: number; pitch: number }) => void }) {
  const map = useMap();
  const fn = React.useRef(onChange);
  fn.current = onChange;
  useEffect(() => {
    if (!map) return;
    let mounted = true;
    let idleListener: any = null;
    let onceListener: any = null;

    const emit = () => {
      const center = map.getCenter?.();
      if (!center) return;
      fn.current?.({
        latitude: center.lat(),
        longitude: center.lng(),
        zoom: map.zoom,
        bearing: map.heading || 0,
        pitch: map.tilt || 0
      });
    };

    // Register after first idle, without using a deferred setTimeout that could leak
    if (typeof map.addListenerOnce === 'function') {
      onceListener = map.addListenerOnce('idle', () => {
        if (!mounted) return;
        idleListener = map.addListener('idle', emit);
      });
    } else {
      // Fallback: ignore first invocation
      let skipped = false;
      idleListener = map.addListener('idle', () => {
        if (!skipped) { skipped = true; return; }
        emit();
      });
    }

    return () => {
      mounted = false;
      onceListener?.remove?.();
      idleListener?.remove?.();
    };
  }, [map]);
  return null;
}


export function useInitFeatures({ features, padding }: { features?: GeoJsonFeature[] | null; padding?: { top: number; bottom: number; left: number; right: number } }) {
  const googlemaps = useMapsLibrary('core');
  const isSmall = useMediaQuery('(max-width: 600px)');
  return React.useMemo(() => {
    if (!googlemaps || !features) return
    const bounds = new googlemaps.LatLngBounds()
    const addFeatureToBounds = (feature: GeoJsonFeature) => {
      const coords = feature?.geometry?.coordinates
      if (coords && Array.isArray(coords) && coords.length >= 2) {
        const lng = coords[0]
        const lat = coords[1]
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          bounds.extend({ lat, lng })
        }
      }
    }

    features?.forEach(addFeatureToBounds)
    const width = window.innerWidth;
    const height = window.innerHeight;
    const defaultPadding = padding || (isSmall
      ? { top: 96, bottom: 280, left: 48, right: 48 }
      : { top: 72, bottom: 240, left: 64, right: 320 });
    const viewport = new WebMercatorViewport({ width, height }).fitBounds(
      [
        [bounds.getSouthWest().lng(), bounds.getSouthWest().lat()],
        [bounds.getNorthEast().lng(), bounds.getNorthEast().lat()],
      ],
      {
        padding: defaultPadding,
      },
    );
    const { longitude, latitude, zoom } = viewport;
    return { longitude, latitude, zoom }
  }, [googlemaps, features, isSmall])  
}


// type CustomZoomControlProps = {
//   controlPosition: ControlPosition;
//   zoom: number;
//   onZoomChange: (zoom: number) => void;
// };

// export const CustomZoomControl = ({
//   controlPosition,
//   zoom,
//   onZoomChange
// }: CustomZoomControlProps) => {
//   return (
//     <MapControl position={controlPosition}>
//       <div
//         style={{
//           margin: '10px',
//           padding: '1em',
//           background: 'rgba(255,255,255,0.4)',
//           display: 'flex',
//           flexFlow: 'column nowrap'
//         }}>
//         <label htmlFor={'zoom'}>This is a custom zoom control!</label>
//         <input
//           id={'zoom'}
//           type={'range'}
//           min={1}
//           max={18}
//           step={'any'}
//           value={zoom}
//           onChange={ev => onZoomChange(ev.target.valueAsNumber)}
//         />
//       </div>
//     </MapControl>
//   );
// };
